import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { WineForm } from '../components/WineForm';
import { WineMediaManager } from '../components/MediaLibrary/WineMediaManager';
import { UserRole } from '@wineryxplorer/shared';
import type { WineFormData, Wine } from '@wineryxplorer/shared';

type TabType = 'details' | 'media';

const TABS: { id: TabType; label: string; description: string }[] = [
  { id: 'details', label: 'Wine Details', description: 'Basic information and wine details' },
  { id: 'media', label: 'Media Management', description: 'Manage hero image and gallery images' },
];

export const WineEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const utils = trpc.useContext();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('details');
  
  // Media state
  const [heroImageId, setHeroImageId] = useState<string | undefined>(undefined);
  const [imageIds, setImageIds] = useState<string[]>([]);
  
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const formSubmitRef = useRef<(() => Promise<void>) | null>(null);

  // Fetch wine data
  const { data: wineData, isLoading, error: fetchError } = trpc.wine.getById.useQuery(
    { id: id || '' },
    { enabled: !!id }
  );

  const wine = wineData as Wine | undefined;

  // The venue management hub links here with `from=manage`, which is the only
  // signal that this edit began there rather than on the wine's own page.
  const [searchParams] = useSearchParams();
  const cameFromManageHub = searchParams.get('from') === 'manage';

  // Extract venueId for permission checking
  const venueId = wine?.venueId;
  const venueIdString = venueId && typeof venueId === 'object' 
    ? (venueId as { _id: string })._id 
    : (venueId as string) || '';

  // Fetch venue data for permission checking
  const { data: venueData } = trpc.venue.getById.useQuery(
    { id: venueIdString },
    { enabled: Boolean(venueIdString) }
  );

  const venue = venueData as { venueAdmins?: string[] } | undefined;

  // Back to wherever this edit started: the venue's management hub, or the
  // wine's own page.
  const exitPath = () =>
    cameFromManageHub && venueIdString ? `/wineries/${venueIdString}/manage` : `/wines/${id}`;

  // Update wine mutation
  const updateWine = trpc.wine.update.useMutation({
    onSuccess: () => {
      setHasUnsavedChanges(false);
      // Invalidate queries to refresh data
      if (id) {
        utils.wine.getById.invalidate({ id });
        utils.wine.list.invalidate();
      }
      navigate(exitPath());
    },
    onError: (error) => {
      setError(error.message);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const handleSubmit = async (data: WineFormData) => {
    if (!id) return;
    
    setError(null);
    // Exclude media fields - they are managed by WineMediaManager which saves directly
    const { heroImageId: _heroImageId, imageIds: _imageIds, ...dataWithoutMedia } = data;
    await updateWine.mutateAsync({
      id,
      ...dataWithoutMedia,
    });
  };

  const handleSaveClick = async () => {
    if (formSubmitRef.current) {
      await formSubmitRef.current();
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmLeave) return;
    }
    navigate(exitPath());
  };

  // Warn on unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Track form changes
  useEffect(() => {
    if (wine && !updateWine.isPending) {
      setHasUnsavedChanges(false);
    }
  }, [wine, updateWine.isPending]);

  // Initialize media state from wine data
  useEffect(() => {
    if (wine) {
      setHeroImageId(wine.heroImageId);
      setImageIds(wine.imageIds || []);
    }
  }, [wine]);

  // Handlers for media changes
  const handleHeroImageChange = (newHeroImageId: string | null) => {
    setHeroImageId(newHeroImageId || undefined);
    setHasUnsavedChanges(true);
  };

  const handleGalleryImagesChange = (newImageIds: string[]) => {
    setImageIds(newImageIds);
    setHasUnsavedChanges(true);
  };

  if (isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-gray-600">Loading wine...</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  if (fetchError || !wine) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-red-600">
              {fetchError?.message || 'Wine not found'}
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Check if user has permission to edit this wine
  const canManage =
    user &&
    (user.role === UserRole.SUPER_ADMIN ||
      user.role === UserRole.SITE_ADMIN ||
      user.id === wine.createdBy ||
      (user.role === UserRole.VENUE_ADMIN && 
       venue?.venueAdmins?.includes(user.id)));

  if (!canManage) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h1>
            <p className="text-red-700 mb-4">
              You don't have permission to edit this wine. Only site administrators, super administrators, 
              and assigned venue administrators can edit wine details.
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Prepare initial data for the form
  const initialData: Partial<WineFormData> = {
    name: wine.name,
    venueId: venueIdString,
    vintage: wine.vintage,
    wineType: wine.wineType,
    varietal: wine.varietal,
    description: wine.description,
    alcoholContent: wine.alcoholContent,
    price: wine.price,
    volume: wine.volume,
    heroImageId: heroImageId,
    imageIds: imageIds,
    countryOfOrigin: wine.countryOfOrigin,
    appellation: wine.appellation,
    subAppellation: wine.subAppellation,
    descriptors: wine.descriptors,
    availability: wine.availability,
    holdUntil: wine.holdUntil,
    drinkBefore: wine.drinkBefore,
    professionalScores: wine.professionalScores,
    appellationNotes: wine.appellationNotes,
    allergens: wine.allergens,
    grapes: wine.grapes,
    status: wine.status,
  };

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="mt-2">
        <div className="border-b border-gray-200 pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Wine</h1>
          
          {/* Action Buttons - Top of Page */}
          <div className="flex gap-4 mb-8 justify-end">
            <Button
              variant="primary"
              onClick={handleSaveClick}
              disabled={updateWine.isPending}
              loading={updateWine.isPending}
            >
              {updateWine.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
          
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {TABS.map((tab) => (
                <Button
                  key={tab.id}
                  variant="ghost"
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </Button>
              ))}
            </nav>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <svg
                className="w-5 h-5 text-red-400 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Error updating wine
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="pt-6">
          {activeTab === 'details' && (
            <WineForm
              initialData={initialData}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              venueId={venueIdString}
              isSubmitting={updateWine.isPending}
              hideButtons
              submitRef={formSubmitRef}
            />
          )}

          {activeTab === 'media' && (
            <WineMediaManager
              wineId={id || ''}
              venueId={venueIdString}
              heroImageId={heroImageId}
              galleryImageIds={imageIds}
              onHeroImageChange={handleHeroImageChange}
              onGalleryImagesChange={handleGalleryImagesChange}
            />
          )}
        </div>
      </PageContainer>
    </div>
  );
};
